'use client';

import { Icon } from '@iconify/react';
import { TodoProject, VoteType } from '@prisma/client';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { motion } from 'framer-motion';
import { Check, Loader2, X } from 'lucide-react';
import { useSession } from 'next-auth/react';
import Image from 'next/image';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { AddTaskForm } from '../../../../(todos)/todos/components/AddTaskForm';
import { Button } from '../../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogOverlay,
  DialogTitle,
} from '../../../../components/ui/dialog';
import { cn } from '../../../../lib/utils';
import { Comment, ICommentable, ItemSeed } from '../../../../types/shared';
// import { RealtimeProvider } from '../../contexts/RealtimeProvider';
import { useTourismItemEvents } from '../../contexts/TourismGlobalEvents';
import { toggleVisitedStatus } from '../../lib/actions/mapActions';
import { getSuggestionDetails, voteOnSuggestion } from '../../lib/actions/suggestionActions';
import { getTourismPlaceDetails, voteOnPlace } from '../../lib/actions/tourismActions';
import { ModalContent } from './ModalContent';

type BaseVote = {
  type: VoteType;
  userId: string;
  [key: string]: unknown;
};

export type Props<T extends ICommentable<BaseVote, Comment>> = {
  itemSeed: ItemSeed;
  itemType: 'place' | 'suggestion';
  onClose: () => void;
  projects?: TodoProject[];
  onProjectsChange?: () => void;
  scrollToCommentId?: string | null;
  onVoteOptimistic?: (itemId: string, voteType: VoteType) => void;
};

function hasVisitedProperty(data: unknown): data is { isVisited: boolean } {
  return (
    typeof data === 'object' &&
    data !== null &&
    'isVisited' in data &&
    typeof (data as { isVisited: unknown }).isVisited === 'boolean'
  );
}

export function ItemViewModal<T extends ICommentable<BaseVote, Comment>>({
  itemSeed,
  itemType,
  onClose,
  projects,
  onProjectsChange,
  scrollToCommentId,
  onVoteOptimistic,
}: Props<T>) {
  const [details, setDetails] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isVisited, setIsVisited] = useState(itemSeed.isVisited || false);
  const [isPlannerOpen, setIsPlannerOpen] = useState(false);
  const [baseUrl, setBaseUrl] = useState('');
  const [likes, setLikes] = useState(itemSeed.initialLikes ?? 0);
  const [dislikes, setDislikes] = useState(itemSeed.initialDislikes ?? 0);
  const [commentCount, setCommentCount] = useState(itemSeed.commentCount ?? 0);
  const [myVote, setMyVote] = useState<VoteType | null>((itemSeed as any).myVote ?? null);

  const modalRef = useRef<HTMLDivElement>(null);
  const { data: session } = useSession();

  const applyOptimisticLocalVote = useCallback((voteType: VoteType) => {
    setMyVote((prev) => {
      // считаем текущие числа из state
      setLikes((curLikes) => {
        setDislikes((curDislikes) => {
          let likesNext = curLikes;
          let dislikesNext = curDislikes;

          // toggle
          if (prev === voteType) {
            if (voteType === VoteType.LIKE) likesNext = Math.max(0, likesNext - 1);
            if (voteType === VoteType.DISLIKE) dislikesNext = Math.max(0, dislikesNext - 1);
            // применим оба
            setLikes(likesNext);
            setDislikes(dislikesNext);
            return curDislikes; // заглушка, мы уже обновили оба
          }

          // switch
          if (prev === VoteType.LIKE) likesNext = Math.max(0, likesNext - 1);
          if (prev === VoteType.DISLIKE) dislikesNext = Math.max(0, dislikesNext - 1);

          if (voteType === VoteType.LIKE) likesNext += 1;
          if (voteType === VoteType.DISLIKE) dislikesNext += 1;

          setLikes(likesNext);
          setDislikes(dislikesNext);
          return curDislikes;
        });

        return curLikes;
      });

      return prev === voteType ? null : voteType;
    });
  }, []);

  useEffect(() => setLikes(itemSeed.initialLikes ?? 0), [itemSeed.initialLikes]);
  useEffect(() => setDislikes(itemSeed.initialDislikes ?? 0), [itemSeed.initialDislikes]);
  useEffect(() => setCommentCount(itemSeed.commentCount ?? 0), [itemSeed.commentCount]);
  useEffect(() => setMyVote((itemSeed as any).myVote ?? null), [itemSeed]);

  // Фокус и закрытие
  useEffect(() => {
    modalRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Загрузка данных
  useEffect(() => {
    let alive = true;

    setIsLoading(true);
    setBaseUrl(window.location.origin);

    const fetchDetails = itemType === 'place' ? getTourismPlaceDetails : getSuggestionDetails;

    fetchDetails(itemSeed.id)
      .then((data) => {
        if (!alive) return;
        if (data) {
          setDetails(data as unknown as T);
          if (hasVisitedProperty(data)) setIsVisited(data.isVisited);
        } else {
          setDetails(null);
        }
      })
      .catch((err) => {
        if (!alive) return;
        console.error(err);
        setDetails(null);
      })
      .finally(() => {
        if (!alive) return;
        setIsLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [itemSeed.id, itemType]);

  // Обработчик "Я здесь был"
  const handleVisitToggle = useCallback(() => {
    if (!details || !session?.user?.id) {
      if (!session?.user?.id) toast.error('Авторизуйтесь, чтобы отметить место');
      return;
    }

    setIsVisited((prev) => !prev);
    toast.promise(toggleVisitedStatus(details.id, itemType), {
      loading: 'Сохранение...',
      success: `Статус обновлен!`,
      error: () => {
        setIsVisited((prev) => !prev); // Откат
        return 'Ошибка обновления';
      },
    });
  }, [details, itemType, session]);

  // Обработчик задач
  const handleTaskAdded = useCallback(() => {
    toast.success('Задача добавлена!');
    setIsPlannerOpen(false);
  }, []);

  const toggleDescription = useCallback(() => {
    setIsDescriptionExpanded((prev) => !prev);
  }, []);

  const jsonLd = useMemo(() => {
    if (!baseUrl) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'TouristAttraction',
      name: itemSeed.title,
      description: itemSeed.description,
      image: itemSeed.imageUrl,
      url: `${baseUrl}/tourism/${itemType === 'place' ? 'sights' : 'sights'}?placeId=${itemSeed.id}`,

    };
  }, [baseUrl, itemSeed, itemType]);

  // Подготовка данных для RealtimeProvider
  const realtimeInitialData = useMemo(() => {
    if (!details) return null;

    return {
      votes: {
        likes: (details as any).initialLikes ?? 0,
        dislikes: (details as any).initialDislikes ?? 0,
      },
      comments: details.comments,
      myVote: (details as any).myVote ?? null,
    };
  }, [details]);

  useEffect(() => {
    if (!details) return;

    setLikes((details as any).initialLikes ?? 0);
    setDislikes((details as any).initialDislikes ?? 0);
    setMyVote((details as any).myVote ?? null);
  }, [details]);

  const itemId = details?.id ?? itemSeed.id;
  const ev = useTourismItemEvents(itemId);

  useEffect(() => {
    if (!ev) return;

    if (ev.type === 'VOTE_UPDATE') {
      if (ev.payload.placeId && ev.payload.placeId !== itemId) return;
      if (ev.payload.suggestionId && ev.payload.suggestionId !== itemId) return;

      setLikes(ev.payload.likes);
      setDislikes(ev.payload.dislikes);
      return;
    }

    if (ev.type === 'NEW_COMMENT') {
      setCommentCount((c) => c + 1);
      return;
    }

    if (ev.type === 'DELETE_COMMENT') {
      const n = ev.payload.commentIds?.length ?? 1;
      setCommentCount((c) => Math.max(0, c - n));
    }
  }, [ev, itemId]);

  const handleVote = useCallback(
    async (voteType: VoteType) => {
      if (!session?.user?.id) {
        toast.error('Войдите, чтобы голосовать');
        return;
      }

      applyOptimisticLocalVote(voteType);
      onVoteOptimistic?.(itemId, voteType);

      const action = itemType === 'place' ? voteOnPlace : voteOnSuggestion;

      try {
        await action(itemId, voteType);
        // SSE пришлёт “истинные” числа
      } catch (e) {
        console.error(e);
        toast.error('Ошибка связи');
        // rollback можно добавить, но обычно SSE быстро поправит состояние после рефетча/обновления
      }
    },
    [session?.user?.id, applyOptimisticLocalVote, onVoteOptimistic, itemId, itemType],
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 md:bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <button
        onClick={onClose}
        className="absolute top-3 right-3 text-white/70 hover:text-white z-50 p-1 rounded-full"
      >
        <X size={28} />
      </button>

      <motion.article
        ref={modalRef}
        tabIndex={-1}
        className="w-full h-full max-w-6xl max-h-[95vh] flex flex-col lg:flex-row gap-0 rounded-xl overflow-hidden shadow-2xl bg-slate-900"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Картинка */}
        <div className="relative w-full aspect-video lg:aspect-auto lg:w-2/3 flex-shrink-0 bg-black">
          <Image
            src={itemSeed.imageUrl}
            alt={itemSeed.title}
            fill
            className="object-cover lg:object-contain"
            priority
          />
        </div>

        {/* Правая колонка */}
        <div className="flex flex-col h-full overflow-y-hidden w-full lg:w-1/3">
          <div className="p-3 sm:p-4 border-b border-slate-700 flex-shrink-0">
            <div className="flex items-start gap-3">
              {isVisited && (
                <motion.div
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="flex-shrink-0 bg-sky-500 text-white rounded-md w-7 h-7 flex items-center justify-center mt-1"
                >
                  <Check className="w-5 h-5" />
                </motion.div>
              )}
              <h2 className="text-xl sm:text-2xl font-bold text-sky-400">{itemSeed.title}</h2>
            </div>

            {itemSeed.description && (
              <p
                className={cn(
                  'mt-2 text-slate-300 text-sm sm:text-base cursor-pointer',
                  !isDescriptionExpanded && 'line-clamp-3 sm:line-clamp-4',
                )}
                onClick={toggleDescription}
              >
                {itemSeed.description}
              </p>
            )}

            <div className="flex flex-wrap gap-2 mt-3 sm:mt-4">
              <Button
                onClick={handleVisitToggle}
                variant={isVisited ? 'secondary' : 'default'}
                size="sm"
              >
                <Icon
                  icon={isVisited ? 'mdi:check-bold' : 'mdi:checkbox-blank-outline'}
                  className="mr-2"
                />
                {isVisited ? 'Я здесь был' : 'Отметить'}
              </Button>
              <Button onClick={() => setIsPlannerOpen(true)} variant="default" size="sm">
                <Icon icon="mdi:plus-box" className="mr-2" />
                Запланировать
              </Button>
            </div>
          </div>

          <div className="flex-grow overflow-y-auto p-3 sm:p-4 min-h-0">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="animate-spin text-sky-400" size={40} />
              </div>
            ) : details ? (
              <ModalContent
                details={details}
                itemType={itemType}
                scrollToCommentId={scrollToCommentId}
                likes={likes}
                dislikes={dislikes}
                myVote={myVote}
                onVote={handleVote}
              />
            ) : (
              <p className="text-center mt-10">Не удалось загрузить данные.</p>
            )}
          </div>
        </div>
      </motion.article>

      {jsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      )}

      {/* Модалка планировщика */}
      <Dialog open={isPlannerOpen} onOpenChange={setIsPlannerOpen}>
        <DialogOverlay className="bg-black/70 backdrop-blur-sm z-[60]" />
        <DialogContent
          className="bg-transparent border-none shadow-none p-0 w-full max-w-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <VisuallyHidden>
            <DialogTitle>Планировщик</DialogTitle>
          </VisuallyHidden>
          <AddTaskForm
            projects={projects || []}
            onTaskAdded={handleTaskAdded}
            onProjectCreated={() => onProjectsChange?.()}
            onCancel={() => setIsPlannerOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
